/**
 * Componente de paginación reutilizable para navegación desktop
 * 
 * @description Implementa paginación tradicional con ventana deslizante de botones.
 * Muestra hasta 5 botones numéricos centrados alrededor de la página actual,
 * junto con botones de navegación "Anterior" y "Siguiente". Optimizado para
 * interfaces desktop con navegación completa y accesibilidad integrada.
 * 
 * **Características:**
 * - Ventana deslizante de 5 botones máximo
 * - Centrado inteligente alrededor de página actual
 * - Validaciones robustas para prevenir crashes
 * - Accesibilidad ARIA completa
 * - Renderizado condicional eficiente
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {number} props.currentPage - Página actual (0-indexed, debe ser ≥ 0)
 * @param {number} props.total - Número total de resultados (debe ser > 0)
 * @param {number} props.pageSize - Resultados por página (debe ser > 0)
 * @param {function} props.onPageChange - Callback ejecutado al cambiar página
 * @param {number} props.onPageChange.newPage - Número de la nueva página (0-indexed)
 * 
 * @returns {JSX.Element|null} Componente de paginación o null si no es necesario
 * 
 * @example
 * // Uso básico con estado local
 * const [currentPage, setCurrentPage] = useState(0);
 * const [searchResults, setSearchResults] = useState({ hits: [], total: 0 });
 * 
 * <Pagination
 *   currentPage={currentPage}
 *   total={searchResults.total}
 *   pageSize={20}
 *   onPageChange={(newPage) => {
 *     setCurrentPage(newPage);
 *     loadPage(newPage);
 *   }}
 * />
 * 
 * @example
 * // Integración con BuscadorApp
 * <Pagination
 *   currentPage={page}
 *   total={data ? data.total : 0}
 *   pageSize={PAGESIZE}
 *   onPageChange={handlePageChange}
 * />
 * 
 * @example
 * // Casos donde no se renderiza (retorna null):
 * // - total <= 0 o pageSize <= 0
 * // - onPageChange no es función
 * // - currentPage < 0
 * // - totalPages <= 1 (solo una página o menos)
 * 
 * @example
 * // Estructura de botones generada:
 * // [Anterior] [1] [2] [3] [4] [5] [Siguiente]
 * //            ^-- Página actual resaltada
 * // 
 * // Para página 10 de 20:
 * // [Anterior] [8] [9] [10] [11] [12] [Siguiente]
 * //                   ^-- Centrado en actual
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ./paginationMobile.js} Versión móvil con "Ver más"
 * @see {@link ../BuscadorApp.js} Componente padre que utiliza paginación
 */
export default function Pagination({ currentPage, total, pageSize, onPageChange }) {
  if (!total || !pageSize || pageSize <= 0) return null;
  if (typeof onPageChange !== 'function') return null;
  if (currentPage < 0) return null;

  const totalPages = Math.ceil(total / pageSize);
  const maxButtons = 5;

  if (totalPages <= 1) return null; // No mostrar paginación si hay solo una página

  let startPage = Math.max(0, currentPage - Math.floor(maxButtons / 2));
  let endPage = Math.min(totalPages, startPage + maxButtons);

  if (endPage - startPage < maxButtons) {
    startPage = Math.max(0, endPage - maxButtons);
  }

  const createButton = (label, page, disabled = false, extraClass = '') => (
    <button
      key={label}
      type="button"
      className={extraClass}
      onClick={() => onPageChange(page)}
      disabled={disabled}
    >
      {label}
    </button>
  );

  return (
    <div id="paginacion" role="navigation" aria-label="Paginación">
      {createButton('Anterior', currentPage - 1, currentPage === 0, 'prev-button')}

      {Array.from({ length: endPage - startPage }, (_, i) => {
        const page = startPage + i;
        return createButton(page + 1, page, false, page === currentPage ? 'activo' : '');
      })}

      {createButton('Siguiente', currentPage + 1, currentPage >= totalPages - 1, 'next-button')}
    </div>
  );
}
